import moment from 'moment-timezone';

// DateInput3 is rendered without timePrecision, so its onChange contract is a bare calendar day
const CALENDAR_DAY_FORMAT = 'YYYY-MM-DD';
const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Resolves the calendar day picked in the deadline date input into the worker local Date
 * the modal keeps in state.
 *
 * The picked day is only a calendar day, it carries no time and no timezone. It has to be
 * applied inside the timezone the deadline is being edited in, keeping the wall clock time
 * that deadline already has in that timezone, so that "end of the day" stays 23:59 for the
 * client instead of being reinterpreted against the worker clock.
 *
 * Returns null when the picker hands over anything other than a valid YYYY-MM-DD day, so an
 * unexpected value is never silently reinterpreted as a deadline.
 */
export const resolveDeadlineDate = (
    pickedDay: string | null,
    currentDate: Date | null,
    editingTimezone: string,
    workerTimezone: string
): Date | null => {
    if (pickedDay === null || currentDate === null) {
        return null;
    }

    if (!CALENDAR_DAY_PATTERN.test(pickedDay)) {
        return null;
    }

    const pickedCalendarDay = moment(pickedDay, CALENDAR_DAY_FORMAT, true);
    if (!pickedCalendarDay.isValid()) {
        return null;
    }

    const deadline = moment(currentDate).tz(editingTimezone);
    if (!deadline.isValid()) {
        return null;
    }

    // Replace the calendar day only, hours/minutes/seconds/milliseconds stay as they are in the
    // timezone being edited, and moment-timezone resolves the offset (including DST) of that day
    deadline.set({
        year: pickedCalendarDay.year(),
        month: pickedCalendarDay.month(),
        date: pickedCalendarDay.date(),
    });

    // Back into the worker local representation the modal state expects
    return new Date(deadline.clone().tz(workerTimezone).format('YYYY-MM-DDTHH:mm:ss.SSS'));
};

export default resolveDeadlineDate;
